import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RotateCcw } from "lucide-react";
import { crmApi } from "./api";
import type { SlaAgentOverride, SlaConfig } from "./types";
import { avatarColorOf, initialsOf } from "./types";
import { ToggleSwitch } from "../../components/ui/toggle-switch";
import { useToast } from "../../components/ui/toast";
import { cn } from "../../lib/utils";

const fieldCls =
  "w-full rounded-lg border border-line bg-transparent px-3 py-2 text-sm text-ink outline-none focus:border-brand disabled:cursor-not-allowed disabled:opacity-50";
const labelCls = "mb-1.5 block text-xs font-semibold text-ink-2";

type AgentDraft = { assignment: string; contact: string };

interface SlaDraft {
  enabled: boolean;
  assignment: string;
  contact: string;
  agents: Record<string, AgentDraft>;
}

function toDraft(config: SlaConfig): SlaDraft {
  const agents: Record<string, AgentDraft> = {};
  for (const [id, override] of Object.entries(config.agents ?? {})) {
    agents[id] = {
      assignment: override.assignment_hours != null ? String(override.assignment_hours) : "",
      contact: override.first_contact_hours != null ? String(override.first_contact_hours) : "",
    };
  }
  return {
    enabled: config.enabled,
    assignment: String(config.assignment_hours),
    contact: String(config.first_contact_hours),
    agents,
  };
}

function SlaForm({ initial, agentList }: { initial: SlaDraft; agentList: { id: string; name: string }[] }) {
  const cache = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState<SlaDraft>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = <K extends keyof SlaDraft>(key: K, value: SlaDraft[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));
  const setAgent = (id: string, key: keyof AgentDraft, value: string) =>
    setDraft((prev) => {
      const current = prev.agents[id] ?? { assignment: "", contact: "" };
      return { ...prev, agents: { ...prev.agents, [id]: { ...current, [key]: value } } };
    });
  const resetAgent = (id: string) =>
    setDraft((prev) => {
      const agents = { ...prev.agents };
      delete agents[id];
      return { ...prev, agents };
    });

  const parseHours = (raw: string): number | null => {
    if (!raw.trim()) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : NaN;
  };

  const save = async () => {
    if (saving) return;
    const assignment = parseHours(draft.assignment);
    const contact = parseHours(draft.contact);
    if (assignment == null || contact == null || !(assignment > 0) || !(contact > 0)) {
      setError("Enter positive hours for both default thresholds.");
      return;
    }
    const agents: Record<string, SlaAgentOverride> = {};
    for (const [id, override] of Object.entries(draft.agents)) {
      const a = parseHours(override.assignment);
      const c = parseHours(override.contact);
      if ((a != null && !(a > 0)) || (c != null && !(c > 0))) {
        setError("Agent overrides must be positive hours or left blank.");
        return;
      }
      if (a != null || c != null) {
        agents[id] = {
          ...(a != null ? { assignment_hours: a } : {}),
          ...(c != null ? { first_contact_hours: c } : {}),
        };
      }
    }
    setSaving(true);
    setError("");
    try {
      await crmApi.updateSla({
        enabled: draft.enabled,
        assignment_hours: assignment,
        first_contact_hours: contact,
        agents,
      });
      await Promise.all([
        cache.invalidateQueries({ queryKey: ["crm-sla"] }),
        cache.invalidateQueries({ queryKey: ["crm-sla-overdue"] }),
      ]);
      toast.success(draft.enabled ? "SLA tracking on" : "SLA tracking off", "Thresholds saved.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to save SLA.");
    } finally {
      setSaving(false);
    }
  };

  const dirty = JSON.stringify({
    enabled: draft.enabled,
    assignment: draft.assignment.trim(),
    contact: draft.contact.trim(),
    agents: draft.agents,
  }) !== JSON.stringify({
    enabled: initial.enabled,
    assignment: initial.assignment,
    contact: initial.contact,
    agents: initial.agents,
  });

  return (
    <div className="space-y-4">
      {/* 1. Master switch */}
      <section className="rounded-xl border border-line bg-elevated p-5 shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-ink">SLA tracking</h2>
            <p className="mt-0.5 text-xs text-ink-2">
              {draft.enabled
                ? "On — unassigned and uncontacted leads count as overdue past the thresholds below."
                : "Off — no lead counts as overdue and breach banners stay hidden."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={cn("text-xs font-semibold", draft.enabled ? "text-emerald-600" : "text-ink-3")}>
              {draft.enabled ? "Enabled" : "Disabled"}
            </span>
            <ToggleSwitch checked={draft.enabled} onChange={(v) => set("enabled", v)} ariaLabel="SLA tracking toggle" />
          </div>
        </div>
      </section>

      {/* 2. Default thresholds */}
      <section className={cn("rounded-xl border border-line bg-elevated p-5 shadow-xs", !draft.enabled && "opacity-60")}>
        <h2 className="text-sm font-semibold text-ink">Default thresholds</h2>
        <p className="mt-0.5 text-xs text-ink-2">
          Apply to every lead unless an agent below has their own override.
        </p>
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Assign within (hours)</label>
            <input
              type="number" min={1} disabled={!draft.enabled}
              value={draft.assignment} onChange={(e) => set("assignment", e.target.value)}
              className={fieldCls}
            />
          </div>
          <div>
            <label className={labelCls}>First contact within (hours)</label>
            <input
              type="number" min={1} disabled={!draft.enabled}
              value={draft.contact} onChange={(e) => set("contact", e.target.value)}
              className={fieldCls}
            />
          </div>
        </div>
      </section>

      {/* 3. Per-agent overrides */}
      <section className={cn("rounded-xl border border-line bg-elevated p-5 shadow-xs", !draft.enabled && "opacity-60")}>
        <h2 className="text-sm font-semibold text-ink">Per-agent overrides</h2>
        <p className="mt-0.5 text-xs text-ink-2">
          Blank means “use the default”. Overrides apply to leads assigned to that agent.
        </p>
        <div className="mt-4 space-y-2">
          {agentList.length === 0 && (
            <p className="text-xs text-ink-3">No active agents to configure.</p>
          )}
          {agentList.map((agent) => {
            const override = draft.agents[agent.id] ?? { assignment: "", contact: "" };
            const customized = (override.assignment.trim() || override.contact.trim()) !== "";
            return (
              <div key={agent.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-line px-3 py-2.5">
                <span className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white", avatarColorOf(agent.id))}>
                  {initialsOf(agent.name)}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">{agent.name}</span>
                <label className="flex items-center gap-1.5 text-xs text-ink-3">
                  Assign
                  <input
                    type="number" min={1} disabled={!draft.enabled} placeholder="Default"
                    value={override.assignment} onChange={(e) => setAgent(agent.id, "assignment", e.target.value)}
                    className="w-24 rounded-lg border border-line bg-transparent px-2 py-1 text-sm text-ink outline-none focus:border-brand disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </label>
                <label className="flex items-center gap-1.5 text-xs text-ink-3">
                  Contact
                  <input
                    type="number" min={1} disabled={!draft.enabled} placeholder="Default"
                    value={override.contact} onChange={(e) => setAgent(agent.id, "contact", e.target.value)}
                    className="w-24 rounded-lg border border-line bg-transparent px-2 py-1 text-sm text-ink outline-none focus:border-brand disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </label>
                {customized && (
                  <button
                    type="button" onClick={() => resetAgent(agent.id)} title="Back to defaults"
                    className="rounded-lg p-1.5 text-ink-3 hover:bg-soft hover:text-ink transition-colors"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {error && <p role="alert" className="text-xs font-semibold text-rose-500">{error}</p>}

      <div className="flex justify-end">
        <button
          disabled={saving || !dirty} onClick={() => void save()}
          className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-40 transition-colors"
        >
          {saving ? "Saving…" : "Save SLA"}
        </button>
      </div>
    </div>
  );
}

export function SlaPage() {
  const team = useQuery({ queryKey: ["crm-team"], queryFn: crmApi.team });
  const sla = useQuery({ queryKey: ["crm-sla"], queryFn: crmApi.sla });
  const overdue = useQuery({ queryKey: ["crm-sla-overdue"], queryFn: crmApi.slaOverdue, refetchInterval: 60000 });

  const canManage = team.data?.role === "admin" || !!team.data?.permissions?.assign_leads;
  const agents = (team.data?.members ?? [])
    .filter((m) => m.active && m.role !== "admin" && m.role !== "")
    .map((m) => ({ id: m.id, name: m.name || m.email || m.id }));

  return (
    <div className="flex h-full flex-col space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-base font-semibold text-ink">SLA</h1>
        {(overdue.data?.length ?? 0) > 0 && (
          <span className="inline-flex items-center gap-2 rounded-lg border border-line bg-elevated px-3 py-1.5 text-xs font-medium text-ink-2">
            <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
            {overdue.data!.length} breaching now
          </span>
        )}
      </div>

      {!canManage && team.data ? (
        <div className="rounded-xl border border-line bg-elevated p-6 text-sm text-ink-2">
          SLA thresholds are managed by sales managers and business admins.
        </div>
      ) : sla.isLoading ? (
        <p className="py-8 text-center text-xs text-ink-3">Loading SLA…</p>
      ) : sla.error || !sla.data ? (
        <p role="alert" className="text-xs font-semibold text-rose-500">
          {(sla.error as Error)?.message || "Unable to load SLA."}
        </p>
      ) : (
        <SlaForm key={JSON.stringify(sla.data)} initial={toDraft(sla.data)} agentList={agents} />
      )}
    </div>
  );
}
